// src/lib/usePager.ts
//
// Keyset-cursor paging state for `GET /api/bills` (Task 5D) — forward-only by
// construction, because a keyset cursor only ever names "the row after this
// one" in ONE direction. "Previous" is answered by replaying the cursor
// history this hook already holds, never by asking the server to page
// backwards; there is no backward cursor to ask for.
//
// ⚠ A cursor is only valid for the exact sort/dir it was minted under —
// `services/billing`'s own guard refuses a mismatched one with 400 (Task 5D).
// So changing sort must restart paging at page 1: the caller resets this
// hook (`reset()`) whenever its own sort state changes, the same way
// `useSort` itself has no opinion on refetching and leaves that to whoever
// reads its state.
//
// (`usePager` itself is a hook and this app has no component-rendering
// harness — see `useSort.test.ts` for the same reasoning. The state machine
// is pure functions below, tested directly in `usePager.test.ts`.)
import { useCallback, useState } from 'react'

export interface PagerState {
  /** 1-based, for display ("Page N"). */
  readonly pageNumber: number
  /** The cursor to send with the CURRENT page's request — `undefined` on page 1. */
  readonly cursor: string | undefined
  /** `history[i]` is the cursor that fetches page `i + 2`. */
  readonly history: readonly string[]
}

export const INITIAL_PAGER_STATE: PagerState = { pageNumber: 1, cursor: undefined, history: [] }

/**
 * Advance to the next page, given the CURRENT page's own `nextCursor`.
 *
 * ⚠ Truncates any forward history past the current page before recording the
 * new cursor. Only reachable by going back and then forward again with a
 * cursor the server did not hand out for that position (the underlying data
 * changed between visits) — truncating rather than trusting stale history
 * keeps the walk self-healing instead of silently reusing a position that may
 * no longer mean the same thing.
 */
export function advancePager(state: PagerState, nextCursor: string): PagerState {
  const history = state.history.slice(0, state.pageNumber - 1)
  history.push(nextCursor)
  return { pageNumber: state.pageNumber + 1, cursor: nextCursor, history }
}

/** Go back one page. A no-op on page 1 — there is nowhere to go back to. */
export function retreatPager(state: PagerState): PagerState {
  if (state.pageNumber <= 1) return state
  const pageNumber = state.pageNumber - 1
  return {
    pageNumber,
    cursor: pageNumber === 1 ? undefined : state.history[pageNumber - 2],
    history: state.history,
  }
}

export interface UsePagerResult {
  readonly pageNumber: number
  readonly cursor: string | undefined
  readonly canGoPrevious: boolean
  /** Call once a page has loaded, with the `nextCursor` IT returned — never `null`; check first. */
  readonly goNext: (nextCursor: string) => void
  readonly goPrevious: () => void
  /** Back to page 1 with no cursor — call whenever the sort this pager walks under changes. */
  readonly reset: () => void
}

export function usePager(): UsePagerResult {
  const [state, setState] = useState<PagerState>(INITIAL_PAGER_STATE)
  const goNext = useCallback((nextCursor: string) => setState((s) => advancePager(s, nextCursor)), [])
  const goPrevious = useCallback(() => setState((s) => retreatPager(s)), [])
  const reset = useCallback(() => setState(INITIAL_PAGER_STATE), [])
  return {
    pageNumber: state.pageNumber,
    cursor: state.cursor,
    canGoPrevious: state.pageNumber > 1,
    goNext,
    goPrevious,
    reset,
  }
}
